import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Pagination from "react-js-pagination";
import {
  Typography,
  LinearProgress,
  Modal,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import NotFound from "../../components/not-found/NotFound";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import { toastMessage } from "../../utils/toastMessage";
import { dateFormat, dateFormatWithTime } from "../../utils/dateFormat";
import { CapitalizeFirstWord } from "../../utils/CapitalizeFirstWord";
import findSerialNumber from "../../utils/findSerialNumber";
import profileImage from "../../Assests/image/profile_not_found.png";
import common from "../../routes/common";
import { user } from "../../routes/route";
import ModalBox from "../../components/modal/ModalBox";
import { getLogsHistoryApi } from "../../services/userManagement";
import { getConsultantOrderHistoryApi } from "../../services/consultantManagement";
import {
  getCustomerProblemListApi,
  getCustomerProblemStatsAllApi,
  saveCustomerRemarkApi,
  getCustomerRemarksBulkApi,
  getCompletedChatCallListApi,
  getCompletedChatCallStatsApi,
  saveCompletedChatCallTagApi,
  removeCompletedChatCallTagApi,
  addCompletedChatCallEvidenceApi,
  addCompletedChatCallNoteEvidenceApi,
  deleteCompletedChatCallEvidenceApi,
  getCompletedChatCallRecordApi,
  getCompletedChatHistoryApi,
  getCompletedChatSummaryStatsApi,
} from "../../services/customerProblemIdentification";
import { getChatHistoryApi } from "../../services/supportManagement";
import { IoSearch, IoCloseCircle } from "react-icons/io5";
import AudioPlayer from "../../components/chatBox/AudioPlayer";

const { NOT_FOUND } = common;
const { USER_DETAIL } = user;

const PROBLEM_CATEGORIES = [
  { id: "completedChatCall",   number: 1,  title: "Completed Chat Call (≤ 5 min)", description: "Chat consultations completed within 5 minutes. Track issues, resolution status and attach evidence." },
  { id: "rechargeUnsuccessful",number: 2,  title: "Recharge – failed / initiated", description: "Failed or initiated attempts (full log incl. success). Removed after recharge succeeds and user completes a consultation." },
  { id: "rechargeNotConsulted",number: 3,  title: "Recharge but not consulted", description: "Wallet top-up succeeded, but no completed consultation happened after the latest recharge." },
  { id: "rechargedNoConsult",  number: 4,  title: "Recharged, Never Consulted",    description: "Registered with wallet balance but never consulted." },
  { id: "shortConsultation",   number: 5,  title: "Short Consultation (1–2 min)",  description: "Consultation ended within 1 to 2 minutes." },
  { id: "customerCutCall",     number: 6,  title: "Customer Cut the Call",          description: "Customer disconnected the call from their side." },
  { id: "missedCancelled",     number: 7,  title: "Missed / Cancelled",             description: "Had one or more missed or cancelled consultation attempts." },
  { id: "rechargedPending",    number: 8,  title: "Recharged – Awaiting Consult",   description: "Registered with wallet balance, no consultation in 30+ days." },
  { id: "impatientInChat",     number: 9,  title: "Short Chat Session",             description: "Chat session ended in under 3 minutes." },
  { id: "switchedConsultant",  number: 10, title: "Switched Consultant",            description: "Consulted with multiple different consultants." },
  { id: "oneTimeUser",         number: 11, title: "One-Time User",                  description: "Completed exactly one consultation (free query excluded) and hasn't returned." },
  { id: "longInactive",        number: 12, title: "Long-Term Inactive",             description: "Consulted 1–5 times but hasn't returned in 30+ days." },
];

const DURATION_FILTERS = [
  { label: "All (≤ 5 min)", value: "lte5" },
  { label: "≤ 1 minute",    value: "lte1" },
  { label: "≤ 2 minutes",   value: "lte2" },
  { label: "≤ 3 minutes",   value: "lte3" },
  { label: "≤ 4 minutes",   value: "lte4" },
  { label: "2 – 5 minutes", value: "range2to5" },
];

const ISSUE_TAG_OPTIONS  = [{ value: "issue", label: "Issue" }, { value: "no_issue", label: "No Issue" }];
const STATUS_TAG_OPTIONS = [{ value: "in_progress", label: "In Progress" }, { value: "solved", label: "Solved" }];
const CATEGORY_TAG_OPTIONS = [{ value: "technical", label: "Technical" }, { value: "consultancy", label: "Consultancy" }];

function formatDurationSecs(secs) {
  if (!secs || secs <= 0) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

const getConsultationRecordingUrl = (consultation = {}) => (
  consultation?.recordingUrl ||
  consultation?.recording_url ||
  consultation?.callRecordingUrl ||
  consultation?.onCompletion?.recordingUrl ||
  consultation?.onCompletion?.recording_url ||
  consultation?.onCompletion?.callRecordingUrl ||
  consultation?.session_info?.recordingUrl ||
  consultation?.session_info?.recording_url ||
  consultation?.session_info?.callRecordingUrl ||
  consultation?.session_info?.sessionMeta?.recordingUrl ||
  consultation?.session_info?.sessionMeta?.recording_url ||
  ""
);

function calcAge(dob) {
  if (!dob) return "—";
  const birth = new Date(dob);
  if (isNaN(birth)) return "—";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age > 0 ? age : "—";
}

const S3_BASE = "https://vedic-meet-bucket.s3.ap-south-1.amazonaws.com/";
const evImgSrc = (url) => url?.startsWith("http") ? url : `${S3_BASE}${url}`;

function issueTagColor(val) {
  if (val === "issue")    return "#b91c1c";
  if (val === "no_issue") return "#15803d";
  return "#64748b";
}

function statusTagColor(val) {
  if (val === "solved")      return "#15803d";
  if (val === "in_progress") return "#b45309";
  if (val === "not_solved")  return "#b91c1c";
  return "#64748b";
}

function statusTagLabel(val) {
  if (val === "solved")      return "Solved";
  if (val === "in_progress") return "In Progress";
  if (val === "not_solved")  return "Not Solved (Auto)";
  return "—";
}

function categoryTagColor(val) {
  if (val === "technical")   return "#0369a1";
  if (val === "consultancy") return "#7c3aed";
  return "#64748b";
}

function categoryTagLabel(val) {
  if (val === "technical")   return "Technical";
  if (val === "consultancy") return "Consultancy";
  return "—";
}

// ── Simple consultation logs list (order-history style) ─────────────────────
const getUniqueConsultationLogs = (logs = []) => {
  const seen = new Set();
  return logs.filter((log) => {
    const key = [
      log.callStatus || "",
      log.callBy || "",
      log.callEndedBy || "",
      log.actionBy || "",
      log.actionValue || "",
      log.cancelledBy || "",
    ].join("|");

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getUniqueSessionLogs = (logs = []) => {
  const seen = new Set();
  return logs.filter((log) => {
    const dataKey = log.logData ? JSON.stringify(log.logData) : "";
    const key = [log.logType || "", log.callStatus || "", dataKey].join("|");

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

function OrderLogModal({ open, onClose, logs }) {
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "initiated":
        return "primary";
      case "started":
        return "success";
      case "ended":
        return "error";
      case "session_extended":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="session-log-modal-title"
      aria-describedby="session-log-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          maxHeight: "80vh",
          bgcolor: "#fafafa",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          p: 4,
          overflow: "auto",
        }}
      >
        <Typography
          id="session-log-modal-title"
          variant="h6"
          component="h2"
          gutterBottom
          sx={{ color: "#333", fontWeight: 600 }}
        >
          Session Logs
        </Typography>
        <Typography
          id="session-log-modal-description"
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ color: "#666" }}
        >
          Detailed timeline of consultation events
        </Typography>

        <List>
          {getUniqueConsultationLogs(Array.isArray(logs) ? logs : []).map((log, index) => {
            return (
              <ListItem
                key={index}
                sx={{
                  border: "1px solid #f0f0f0",
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                    borderColor: "#e9ecef",
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={log.callStatus}
                        color={getStatusColor(log.callStatus)}
                        size="small"
                        sx={{
                          backgroundColor:
                            getStatusColor(log.callStatus) === "primary"
                              ? "#e3f2fd"
                              : getStatusColor(log.callStatus) === "success"
                              ? "#e8f5e8"
                              : getStatusColor(log.callStatus) === "error"
                              ? "#ffebee"
                              : getStatusColor(log.callStatus) === "warning"
                              ? "#fff3e0"
                              : "#f5f5f5",
                          color:
                            getStatusColor(log.callStatus) === "primary"
                              ? "#1976d2"
                              : getStatusColor(log.callStatus) === "success"
                              ? "#2e7d32"
                              : getStatusColor(log.callStatus) === "error"
                              ? "#d32f2f"
                              : getStatusColor(log.callStatus) === "warning"
                              ? "#ed6c02"
                              : "#666",
                        }}
                      />
                      {log.callBy && (
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          by {log.callBy}
                        </Typography>
                      )}
                      {log.callEndedBy && (
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          ended by {log.callEndedBy}
                        </Typography>
                      )}
                      {log.actionBy && (
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {log.actionBy}
                        </Typography>
                      )}
                      {log.actionValue && (
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          ({log.actionValue}s)
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{ color: "#888", fontSize: "0.875rem" }}
                    >
                      {formatTimestamp(log.timestamp)}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            onClick={onClose}
            sx={{
              backgroundColor: "#f0f0f0",
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
          >
            Close
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
}

function LogCard({ log }) {
  const [expanded, setExpanded] = React.useState(false);
  const data = log?.logData || {};

  const isBad = data.speedKbps !== undefined && data.speedKbps !== null && data.speedKbps < 5;
  const isWarn = data.responseTimeMs !== undefined && data.responseTimeMs !== null && data.responseTimeMs > 2000;

  function formatTimeHHMMSS(iso) {
    if (!iso) return "--:--:--";
    const d = new Date(iso);
    return d.toLocaleTimeString("en-GB", { hour12: false });
  }

  return (
    <div
      className={`log-timeline-card${isBad ? " log-card-bad" : isWarn ? " log-card-warning" : ""}`}
    >
      <div className="log-main-row">
        <span className="log-dot" />
        <span className="log-time">{formatTimeHHMMSS(log.createdAt)}</span>
        <span className="log-type">
          {data.type ? data.type.charAt(0).toUpperCase() + data.type.slice(1) : ""}
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "21px", marginTop: "7px" }}>
        <div>
          <span className="log-section-label">Speed: </span>
          <span className={"log-section-value " + (isBad ? "log-red" : "")}>
            {data.speedKbps !== undefined && data.speedKbps !== null ? `${data.speedKbps} kbps` : "--"}
          </span>
        </div>

        <div>
          <span className="log-section-label">Resp: </span>
          <span className={"log-section-value " + (isWarn ? "log-card-warning" : "")}>
            {data.responseTimeMs !== undefined && data.responseTimeMs !== null ? `${data.responseTimeMs} ms` : "--"}
          </span>
        </div>

        {data.carrier && (
          <div>
            <span className="log-section-label">Carrier: </span>
            <span className="log-section-value">{data.carrier}</span>
          </div>
        )}

        {data.strength !== undefined && (
          <div>
            <span className="log-section-label">Strength: </span>
            <span className="log-section-value">{data.strength}</span>
          </div>
        )}
      </div>

      <div className="log-card-actions">
        <button className="log-more-btn" onClick={() => setExpanded(!expanded)}>
          {expanded ? "Show Less" : "Show More"}
        </button>
      </div>

      {expanded && <div className="log-card-raw">{JSON.stringify(data, null, 2)}</div>}
    </div>
  );
}

function msgTypeName(t) {
  const map = { 1: "Text", 2: "Video", 3: "Audio", 4: "PDF", 5: "File", 6: "Audio Call", 7: "Video Call" };
  return map[t] || "Message";
}

function formatDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
}

const DATE_PRESETS = [
  { label: "Today",       value: "today" },
  { label: "Yesterday",   value: "yesterday" },
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last 30 Days",value: "last30days" },
  { label: "This Month",  value: "thisMonth" },
  { label: "Last Month",  value: "lastMonth" },
];

const PENDING_SINCE_BUCKETS = [
  { label: "All pending days", value: "" },
  { label: "1-2 days", value: "1_2_days" },
  { label: "3-7 days", value: "3_7_days" },
  { label: "8-14 days", value: "8_14_days" },
  { label: "15-21 days", value: "15_21_days" },
  { label: "22-30 days", value: "22_30_days" },
];

function formatElapsedSince(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return "—";
  const s = Math.floor(Number(ms) / 1000);
  if (s < 0) return "—";
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m`;
  return "< 1m";
}

function formatTxnStatus(s) {
  const x = String(s || "").toLowerCase();
  if (x === "paid" || x === "completed") return "Success";
  if (x === "consulted") return "Consulted";
  if (x === "failed") return "Failed";
  if (x === "initiated") return "Initiated";
  return CapitalizeFirstWord(String(s || "—"));
}

function txnStatusToneClass(status) {
  const x = String(status || "").toLowerCase();
  if (x === "paid" || x === "completed" || x === "consulted") return "success";
  if (x === "failed") return "failed";
  if (x === "initiated") return "pending";
  return "neutral";
}

function resolveAttemptAmount(entry) {
  const candidates = [
    entry?.paidAmount,
    entry?.amount,
    entry?.rechargeAmount,
    entry?.coins,
    entry?.totalAmount,
    entry?.txnAmount,
    entry?.value,
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const normalized =
      typeof c === "string"
        ? c.replace(/[^\d.-]/g, "")
        : c;
    const n = Number(normalized);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function asFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getRechargeStatusCounts(userRow) {
  const failedCandidates = [
    userRow?.failedCount,
    userRow?.failCount,
    userRow?.failedAttempts,
    userRow?.totalFailed,
    userRow?.statusCounts?.failed,
    userRow?.attemptCounts?.failed,
  ];
  const initiatedCandidates = [
    userRow?.initiatedCount,
    userRow?.initCount,
    userRow?.initiatedAttempts,
    userRow?.totalInitiated,
    userRow?.statusCounts?.initiated,
    userRow?.attemptCounts?.initiated,
  ];
  const successCandidates = [
    userRow?.successCount,
    userRow?.paidCount,
    userRow?.completedCount,
    userRow?.totalSuccess,
    userRow?.statusCounts?.success,
    userRow?.statusCounts?.paid,
    userRow?.attemptCounts?.success,
  ];

  const pickedFailed = failedCandidates.map(asFiniteNumber).find((v) => v != null);
  const pickedInitiated = initiatedCandidates.map(asFiniteNumber).find((v) => v != null);
  const pickedSuccess = successCandidates.map(asFiniteNumber).find((v) => v != null);

  const log = Array.isArray(userRow?.rechargeAttemptLog) ? userRow.rechargeAttemptLog : [];
  const logCounts = log.reduce(
    (acc, item) => {
      const status = String(item?.status || "").toLowerCase();
      if (status === "failed") acc.failed += 1;
      if (status === "initiated") acc.initiated += 1;
      if (status === "paid" || status === "completed" || status === "success") acc.success += 1;
      return acc;
    },
    { failed: 0, initiated: 0, success: 0 }
  );

  return {
    failed: pickedFailed ?? logCounts.failed,
    initiated: pickedInitiated ?? logCounts.initiated,
    success: pickedSuccess ?? logCounts.success,
  };
}

function getLatestSuccessMeta(userRow) {
  const successDateCandidates = [
    userRow?.lastSuccessAt,
    userRow?.successAt,
    userRow?.paidAt,
    userRow?.completedAt,
    userRow?.lastSuccessInWindow,
    userRow?.firstSuccessAfterWindow,
    userRow?.nextSuccessAt,
  ];
  const fromDirectFields = successDateCandidates.find((d) => !!d);
  if (fromDirectFields) {
    return { status: "Success", at: fromDirectFields };
  }

  const log = Array.isArray(userRow?.rechargeAttemptLog) ? userRow.rechargeAttemptLog : [];
  const successEvents = log
    .filter((item) => {
      const st = String(item?.status || "").toLowerCase();
      return st === "paid" || st === "completed" || st === "success";
    })
    .filter((item) => item?.at)
    .sort((a, b) => new Date(b.at) - new Date(a.at));

  if (successEvents.length > 0) {
    return { status: formatTxnStatus(successEvents[0].status), at: successEvents[0].at };
  }
  return null;
}

function normalizePaymentMethod(value) {
  const x = String(value || "").trim().toLowerCase();
  if (!x) return "---";
  if (x === "phonepe") return "PhonePe";
  if (x === "paytm") return "Paytm";
  if (x === "razorpay") return "Razorpay";
  if (x === "cashfree") return "Cashfree";
  if (x === "upi") return "UPI";
  return CapitalizeFirstWord(x);
}

function getLatestRechargeEntry(userRow) {
  const history = Array.isArray(userRow?.rechargeHistory) ? userRow.rechargeHistory : [];
  if (history.length > 0) {
    const latest = [...history].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    return {
      at: latest?.createdAt,
      status: latest?.status || "paid",
      rechargeAmount: latest?.rechargeAmount ?? latest?.paidAmount,
      orderId: latest?.orderId,
      paymentMethod: latest?.paymentMethod || latest?.payment_mode || latest?.paymentGateway || latest?.method || latest?.pgType || latest?.gateway || latest?.provider,
      type: "Recharge",
    };
  }
  if (userRow?.rechargeAt) {
    return {
      at: userRow.rechargeAt,
      status: "paid",
      rechargeAmount: userRow.rechargeAmount,
      orderId: userRow.orderId,
      paymentMethod: userRow.paymentMethod || userRow.payment_mode || userRow.paymentGateway || userRow.method || userRow.pgType || userRow.gateway || userRow.provider,
      type: "Recharge",
    };
  }
  return null;
}

// ── Remark inline editor component ──────────────────────────────────────────
const RemarkCell = ({ userId, problemType, contextKey, initialRemark, onSaved }) => {
  const [editing, setEditing]   = useState(false);
  const [value, setValue]       = useState(initialRemark || "");
  const [saving, setSaving]     = useState(false);
  const textareaRef             = useRef(null);

  useEffect(() => { setValue(initialRemark || ""); }, [initialRemark, contextKey]);

  const openEdit = () => {
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await saveCustomerRemarkApi({
        userId,
        problemType,
        remark: value.trim(),
        ...(contextKey ? { contextKey } : {}),
      });
      if (data?.code === 200) {
        onSaved(userId, value.trim(), contextKey);
        setEditing(false);
        toastMessage("Remark saved", "success", "remark-save");
      } else {
        toastMessage(data?.message || "Failed to save", "error", "remark-err");
      }
    } catch {
      toastMessage("Error saving remark", "error", "remark-err");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(initialRemark || "");
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="cp-remark-editor">
        <textarea
          ref={textareaRef}
          className="cp-remark-textarea"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter action taken / summary..."
          rows={3}
        />
        <div className="cp-remark-actions">
          <button
            className="cp-remark-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            <CheckIcon sx={{ fontSize: 14 }} />
            {saving ? "Saving…" : "Save"}
          </button>
          <button className="cp-remark-cancel-btn" onClick={handleCancel}>
            <CloseIcon sx={{ fontSize: 14 }} />
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-remark-display" onClick={openEdit} title="Click to add/edit remark">
      {value ? (
        <span className="cp-remark-text">{value}</span>
      ) : (
        <span className="cp-remark-empty">Add remark…</span>
      )}
      <EditIcon className="cp-remark-edit-icon" sx={{ fontSize: 13 }} />
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const CustomerProblemIdentification = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  const activePage        = +searchParam.get("page")  || 1;
  const pageLimit         = +searchParam.get("limit") || 10;
  const selectedProblemId =  searchParam.get("problem") || "";

  const [search, setSearch]           = useState("");
  const [userList, setUserList]       = useState([]);
  const [total, setTotal]             = useState(0);
  const [problemStats, setProblemStats]   = useState({});
  const [statsLoading, setStatsLoading]   = useState({});
  const [remarks, setRemarks]         = useState({});   // { userId: remarkText }
  const [attemptLogModal, setAttemptLogModal] = useState({
    open: false,
    title: "",
    heading: "Recharge attempt log",
    methodHeader: "Activity / Payment method",
    log: [],
  });
  const [orderLogsModal, setOrderLogsModal] = useState({ open: false, logs: [] });

  // ── Consultation Network Logs modal (user + consultant session tracking) ──
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [userLogs, setUserLogs] = useState([]);
  const [consultantLogs, setConsultantLogs] = useState([]);

  // ── Completed Chat Call state ──────────────────────────────────────────────
  const [chatCallList, setChatCallList] = useState([]);
  const [chatCallTotal, setChatCallTotal] = useState(0);
  const [durationFilter, setDurationFilter] = useState("lte5");
  const [issueTagFilter, setIssueTagFilter]   = useState("");
  const [statusTagFilter, setStatusTagFilter] = useState("");
  const [categoryTagFilter, setCategoryTagFilter] = useState("");

  // Tag management dialog
  const [tagModal, setTagModal] = useState({
    open: false, waitlistId: null, userId: null, consultantId: null,
    issueTag: null, statusTag: null, categoryTag: null, tagHistory: [], evidence: [],
    savingIssue: false, savingStatus: false, savingCategory: false,
  });

  // Evidence dialog
  const [evidenceModal, setEvidenceModal] = useState({
    open: false, waitlistId: null, userId: null, consultantId: null,
    evidence: [], noteText: "", evidenceType: "whatsapp", uploading: false,
    selectedFiles: [], audioFile: null,
  });

  // History dialog
  const [historyModal, setHistoryModal] = useState({ open: false, history: [], title: "" });

  // Chat history dialog (real messages)
  const [chatHistoryModal, setChatHistoryModal] = useState({
    open: false, messages: [], loading: false, loadingMore: false,
    title: "", row: null, userId: null, threadId: null,
    page: 1, totalPages: 1,
    sessionStart: null, sessionEnd: null,
    recordingUrl: "", orderId: "", consultationMode: "",
  });

  // Summary stats bar (tag counts)
  const [summaryStats, setSummaryStats] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Specific datetime filter for completedChatCall (overrides date preset)
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime]     = useState("");

  // Date filter — default Today
  const [selectedPreset, setSelectedPreset]   = useState("today");
  const [rangeStart, setRangeStart]           = useState("");
  const [rangeEnd, setRangeEnd]               = useState("");
  const [dateFilterLabel, setDateFilterLabel] = useState("Today");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [pendingSinceBucket, setPendingSinceBucket] = useState("");

  const activeStatsFilterRef = useRef({ timeRange: "today" });
  const [statsVersion, setStatsVersion]           = useState(0);
  const [listFilterVersion, setListFilterVersion] = useState(0);

  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const debounceValue = useDebounce(search, 400);
  const tableRef      = useRef(null);
  const chatBodyRef   = useRef(null);
  const chatHistoryModalRef = useRef(null);

  const selectedProblem = PROBLEM_CATEGORIES.find((p) => p.id === selectedProblemId);

  const buildFilter = useCallback(() => {
    const f = {};
    // For completedChatCall, datetime filter overrides date preset
    if (selectedProblemId === "completedChatCall" && (startDateTime || endDateTime)) {
      if (startDateTime) f.startDate = startDateTime;
      if (endDateTime)   f.endDate   = endDateTime;
    } else if (selectedPreset && selectedPreset !== "custom") {
      f.timeRange = selectedPreset;
    } else if (selectedPreset === "custom") {
      if (rangeStart) f.startDate = rangeStart;
      if (rangeEnd)   f.endDate   = rangeEnd;
    }
    if (selectedProblemId === "rechargeNotConsulted" && pendingSinceBucket) {
      f.pendingSinceBucket = pendingSinceBucket;
    }
    return f;
  }, [selectedPreset, rangeStart, rangeEnd, selectedProblemId, pendingSinceBucket, startDateTime, endDateTime]);

  const triggerStatsReload = useCallback((filter) => {
    activeStatsFilterRef.current = filter;
    setStatsVersion((v) => v + 1);
    setListFilterVersion((v) => v + 1);
  }, []);

  // Fetch user list + remarks
  const fetchUserList = useCallback(async () => {
    if (!selectedProblemId) return;
    try {
      showLoader();
      const sendData = {
        problemType: selectedProblemId,
        page: String(activePage),
        limit: String(pageLimit),
        filter: buildFilter(),
      };
      if (debounceValue) sendData.search = debounceValue;
      const { data } = await getCustomerProblemListApi(sendData);
      if (data?.code === 200) {
        const list = data?.result?.list || [];
        setUserList(list);
        setTotal(data?.result?.total || 0);
        // Fetch remarks for these users
        if (list.length > 0) {
          try {
            if (selectedProblemId === "rechargeNotConsulted") {
              const remarkTargets = list
                .map((u) => ({ userId: u._id, contextKey: u.rechargeTransactionId }))
                .filter((t) => t.userId && t.contextKey);
              const { data: rd } = await getCustomerRemarksBulkApi({
                problemType: selectedProblemId,
                remarkTargets,
              });
              if (rd?.code === 200) {
                const map = {};
                (rd.result || []).forEach((r) => {
                  const k = `${String(r.userId)}::${String(r.contextKey || "")}`;
                  map[k] = r.remark || "";
                });
                setRemarks(map);
              }
            } else {
              const ids = list.map((u) => u._id).filter(Boolean);
              const { data: rd } = await getCustomerRemarksBulkApi({
                userIds: ids,
                problemType: selectedProblemId,
              });
              if (rd?.code === 200) {
                const map = {};
                (rd.result || []).forEach((r) => { map[String(r.userId)] = r.remark || ""; });
                setRemarks(map);
              }
            }
          } catch { /* silent */ }
        }
      } else {
        setUserList([]);
        setTotal(0);
        toastMessage(data?.message || "Failed to fetch users", "error", "cpList");
      }
    } catch (err) {
      console.error("fetchUserList error:", err);
    } finally {
      hideLoader();
    }
  }, [selectedProblemId, activePage, pageLimit, debounceValue, buildFilter]);

  // Fetch completed chat call list + summary stats in parallel
  const fetchChatCallList = useCallback(async () => {
    try {
      showLoader();
      setSummaryLoading(true);
      const baseFilter = {
        ...buildFilter(),
        ...(issueTagFilter  ? { issueTag:  issueTagFilter }  : {}),
        ...(statusTagFilter ? { statusTag: statusTagFilter } : {}),
        ...(categoryTagFilter ? { categoryTag: categoryTagFilter } : {}),
      };
      const sendData = {
        page: String(activePage),
        limit: String(pageLimit),
        durationFilter,
        filter: baseFilter,
      };
      if (debounceValue) sendData.search = debounceValue;

      const [listRes, statsRes] = await Promise.allSettled([
        getCompletedChatCallListApi(sendData),
        getCompletedChatSummaryStatsApi({ filter: buildFilter(), durationFilter }),
      ]);

      if (listRes.status === "fulfilled" && listRes.value?.data?.code === 200) {
        setChatCallList(listRes.value.data?.result?.list || []);
        setChatCallTotal(listRes.value.data?.result?.total || 0);
      } else {
        setChatCallList([]);
        setChatCallTotal(0);
        if (listRes.status === "fulfilled") toastMessage(listRes.value?.data?.message || "Failed to fetch", "error", "ccList");
      }

      if (statsRes.status === "fulfilled" && statsRes.value?.data?.code === 200) {
        setSummaryStats(statsRes.value.data.result);
      }
    } catch (err) {
      console.error("fetchChatCallList error:", err);
    } finally {
      hideLoader();
      setSummaryLoading(false);
    }
  }, [activePage, pageLimit, durationFilter, issueTagFilter, statusTagFilter, categoryTagFilter, debounceValue, buildFilter]);

  // Load card stats — single bulk request (all 9 types run in parallel on server)
  useEffect(() => {
    let cancelled = false;
    const filter  = activeStatsFilterRef.current;
    setProblemStats({});
    const loadingMap = {};
    PROBLEM_CATEGORIES.forEach((p) => { loadingMap[p.id] = true; });
    setStatsLoading(loadingMap);

    const loadAll = async () => {
      try {
        const { data } = await getCustomerProblemStatsAllApi(filter);
        if (cancelled) return;
        if (data?.code === 200) {
          setProblemStats(data.result || {});
        }
      } catch { /* silent */ }
      finally {
        if (!cancelled) setStatsLoading({});
      }
    };
    loadAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [statsVersion]);

  useEffect(() => {
    if (selectedProblemId === "completedChatCall") fetchChatCallList();
    else if (selectedProblemId) fetchUserList();
    // eslint-disable-next-line
  }, [selectedProblemId, activePage, pageLimit, debounceValue, listFilterVersion, pendingSinceBucket, durationFilter, issueTagFilter, statusTagFilter, categoryTagFilter]);

  const handleSelectProblem = (id) => {
    if (selectedProblemId === id) searchParam.delete("problem");
    else searchParam.set("problem", id);
    searchParam.delete("page");
    setPendingSinceBucket("");
    setSearch("");
    setSearchParam(searchParam);
    setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handlePageChange     = (page) => { searchParam.set("page", page); setSearchParam(searchParam); };
  const handlePageLimitChange = (e)  => { searchParam.set("limit", e.target.value); searchParam.delete("page"); setSearchParam(searchParam); };
  const handleClearSelection  = ()   => { searchParam.delete("problem"); searchParam.delete("page"); searchParam.delete("limit"); setSearch(""); setPendingSinceBucket(""); setSearchParam(searchParam); setUserList([]); setTotal(0); setChatCallList([]); setChatCallTotal(0); setDurationFilter("lte5"); setIssueTagFilter(""); setStatusTagFilter(""); setCategoryTagFilter(""); setSummaryStats(null); setStartDateTime(""); setEndDateTime(""); };

  // Date filter
  const handleDateSelectChange = (e) => {
    const val = e.target.value;
    if (val === "custom") {
      setSelectedPreset("custom");
      setShowCustomRange(true);
    } else {
      setSelectedPreset(val);
      setShowCustomRange(false);
      setRangeStart(""); setRangeEnd("");
      const label = DATE_PRESETS.find((p) => p.value === val)?.label || "All Time";
      setDateFilterLabel(label);
      triggerStatsReload(val ? { timeRange: val } : {});
    }
  };

  const applyCustomRange = () => {
    if (!rangeStart && !rangeEnd) return;
    const fmt = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "…";
    setDateFilterLabel(`${fmt(rangeStart)} – ${fmt(rangeEnd)}`);
    const rf = {};
    if (rangeStart) rf.startDate = rangeStart;
    if (rangeEnd)   rf.endDate   = rangeEnd;
    triggerStatsReload(rf);
  };

  const handleRemarkSaved = (userId, text, contextKey) => {
    const k = contextKey ? `${String(userId)}::${String(contextKey)}` : String(userId);
    setRemarks((prev) => ({ ...prev, [k]: text }));
  };

  const remarkKeyForRow = (u) => {
    if (selectedProblemId === "rechargeNotConsulted" && u.rechargeTransactionId) {
      return `${String(u._id)}::${String(u.rechargeTransactionId)}`;
    }
    return String(u._id);
  };

  const openRechargeConsultHistory = (u) => {
    const latestRecharge = getLatestRechargeEntry(u);
    const log = latestRecharge ? [latestRecharge] : [];
    setAttemptLogModal({
      open: true,
      title: u.name || u.userId || "User",
      heading: "Last recharge history",
      methodHeader: "Payment method",
      log,
    });
  };

  const handleShowOrderLogs = async (row) => {
    const waitlistId = String(row?._id || "");
    if (!waitlistId) return;

    let logs = Array.isArray(row.logs) ? row.logs : [];

    // If logs not already present on the row, try fetching from completed-chat-call record
    if (!logs.length) {
      try {
        const { data } = await getCompletedChatCallRecordApi(waitlistId);
        if (data?.code === 200 && Array.isArray(data.result?.logs)) {
          logs = data.result.logs;
        }
      } catch (err) {
        // ignore here; we'll fall back to consultant order history
      }
    }

    // Fallback: mirror Consultant Management order_history behaviour
    // Fetch consultant's order history and locate matching waitlist by _id to get logs
    if (!logs.length && row.rawConsultantId) {
      try {
        const { data } = await getConsultantOrderHistoryApi({
          page: 1,
          limit: 50,
          consultantId: row.rawConsultantId,
        });

        // API response shape differs across environments:
        // - { code:200, result:{ list:[...] } }
        // - { success:true, result:{ code:200, result:{ list:[...] } } }
        const list =
          data?.result?.list ||
          data?.result?.result?.list ||
          [];

        if (Array.isArray(list) && list.length) {
          const match = list.find((o) => String(o._id) === waitlistId);
          if (match?.logs?.length) {
            logs = match.logs;
            // Cache on the CPI row so next time no API hit
            setChatCallList((prev) =>
              prev.map((r) =>
                String(r._id) === waitlistId ? { ...r, logs: match.logs } : r
              )
            );
          }
        }
      } catch (err) {
        toastMessage(
          err?.response?.data?.message || err?.message || "Failed to load session logs",
          "error",
          "sess-logs"
        );
      }
    }

    const uniqueLogs = getUniqueConsultationLogs(logs);
    if (uniqueLogs && uniqueLogs.length > 0) {
      setOrderLogsModal({ open: true, logs: uniqueLogs });
    } else {
      toastMessage("No logs available for this consultation", "info", "sess-logs-empty");
    }
  };

  // Keep chatHistoryModalRef in sync with state (used by stable scroll callback)
  useEffect(() => { chatHistoryModalRef.current = chatHistoryModal; }, [chatHistoryModal]);

  // Stable load-more — reads from ref so scroll handler never captures stale state
  const doLoadMoreChat = useCallback(async () => {
    const modal = chatHistoryModalRef.current;
    if (!modal || modal.loadingMore || modal.page >= modal.totalPages || !modal.threadId) return;

    setChatHistoryModal((m) => ({ ...m, loadingMore: true }));
    try {
      const nextPage = modal.page + 1;
      const res = await getChatHistoryApi({ threadId: modal.threadId, page: nextPage, limit: 50 });
      const msgs = res?.data?.result?.messages || [];
      const totalPages = res?.data?.result?.totalPages || modal.totalPages;

      const el = chatBodyRef.current;
      const prevHeight = el?.scrollHeight || 0;
      setChatHistoryModal((m) => ({
        ...m, loadingMore: false, page: nextPage,
        totalPages,
        messages: [...msgs, ...m.messages],
      }));
      if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - prevHeight; });
    } catch {
      setChatHistoryModal((m) => ({ ...m, loadingMore: false }));
    }
  }, []); // empty deps — all reads go through refs / stable setters

  // Scroll-up listener: load older pages when user reaches top
  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el || !chatHistoryModal.open) return;
    const onScroll = () => { if (el.scrollTop < 150) doLoadMoreChat(); };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [chatHistoryModal.open, doLoadMoreChat]);

  // Scroll to bottom after initial load (page === 1)
  useEffect(() => {
    if (!chatHistoryModal.loading && chatHistoryModal.page === 1 && chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistoryModal.loading]);

  // ── Consultation Network Logs (chat-server) ───────────────────────────────
  const handleCloseLogsModal = () => {
    setLogsModalOpen(false);
    setUserLogs([]);
    setConsultantLogs([]);
    setLogsError("");
  };

  const handleViewNetworkLogs = async (waitlistId) => {
    const wlId = String(waitlistId || "");
    if (!wlId) return;

    setLogsError("");
    setUserLogs([]);
    setConsultantLogs([]);
    setLogsLoading(true);

    try {
      const res = await getLogsHistoryApi({ waitlistId: wlId });
      const logsArr = res?.data?.data?.logs || [];
      const decoratedLogs = getUniqueSessionLogs(logsArr).map((l) => ({ ...l, showDetails: false }));

      setUserLogs(decoratedLogs.filter((l) => l.logType === "user_session_tracking"));
      setConsultantLogs(decoratedLogs.filter((l) => l.logType === "consultant_session_tracking"));
      setLogsModalOpen(true);
      setLogsError(
        logsArr.length === 0 ? (res?.data?.message || res?.message || "No logs found.") : ""
      );
    } catch (err) {
      setLogsError(err?.message || "Failed to fetch logs.");
      setUserLogs([]);
      setConsultantLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  // ── Chat history ───────────────────────────────────────────────────────────
  const openChatHistory = async (row) => {
    const threadId   = row.threadId || null;
    const userId     = String(row.userObjectId || row.rawUserId || "");
    const recordingUrl = getConsultationRecordingUrl(row);
    const consultationMode = row.mode || row.consultationMode || row.session_info?.mode || "";
    const recordingOnly = Boolean(recordingUrl) && String(consultationMode).toLowerCase() === "audio";
    const sessionStart = new Date(row.callStartTime || row.consultationDate).getTime();
    const sessionEnd   = sessionStart + (row.durationSeconds || 300) * 1000;
    const BUFFER_MS    = 5 * 60 * 1000;

    setChatHistoryModal({
      open: true, messages: [], loading: true, loadingMore: false,
      title: "", row, userId, threadId, page: 1, totalPages: 1,
      sessionStart, sessionEnd,
      recordingUrl,
      orderId: row.orderId || "",
      consultationMode,
    });
    try {
      if (recordingOnly) {
        setChatHistoryModal((m) => ({ ...m, loading: false, messages: [] }));
      } else if (threadId) {
        // Search up to 6 pages newest-first to find the consultation time window
        const LIMIT = 50;
        const MAX_SEARCH = 6;
        let foundMsgs = [];
        let lastPage  = 1;
        let totalPages = 1;

        for (let p = 1; p <= MAX_SEARCH; p++) {
          const res = await getChatHistoryApi({ threadId, page: p, limit: LIMIT });
          const msgs = res?.data?.result?.messages || [];
          totalPages = res?.data?.result?.totalPages || 1;
          lastPage   = p;

          const windowMsgs = msgs.filter((msg) => {
            if (!msg.createdAt) return false;
            const t = new Date(msg.createdAt).getTime();
            return t >= (sessionStart - BUFFER_MS) && t <= (sessionEnd + BUFFER_MS);
          });

          if (windowMsgs.length > 0) { foundMsgs = windowMsgs; break; }

          // If this page's newest message is already before our window, subsequent pages are even older
          if (msgs.length > 0 && msgs[0]?.createdAt) {
            if (new Date(msgs[0].createdAt).getTime() < sessionStart - BUFFER_MS) break;
          }
          if (p >= totalPages) break;
        }

        setChatHistoryModal((m) => ({ ...m, loading: false, messages: foundMsgs, page: lastPage, totalPages }));
      } else {
        const { data } = await getCompletedChatHistoryApi(String(row._id));
        setChatHistoryModal((m) => ({ ...m, loading: false, messages: data?.result || [] }));
      }
    } catch {
      setChatHistoryModal((m) => ({ ...m, loading: false }));
      toastMessage("Failed to load chat history", "error", "chat-hist");
    }
  };

  // ── Tag management helpers ─────────────────────────────────────────────────
  const openTagModal = async (row) => {
    setTagModal({
      open: true,
      waitlistId: String(row._id),
      userId: row.userObjectId ? String(row.userObjectId) : null,
      consultantId: null,
      issueTag: row.tagRecord?.issueTag || null,
      statusTag: row.tagRecord?.statusTag || null,
      categoryTag: row.tagRecord?.categoryTag || null,
      tagHistory: row.tagRecord?.tagHistory || [],
      evidence: row.tagRecord?.evidence || [],
      savingIssue: false,
      savingStatus: false,
      savingCategory: false,
    });
  };

  const refreshTagRecord = async (waitlistId) => {
    try {
      const { data } = await getCompletedChatCallRecordApi(waitlistId);
      if (data?.code === 200 && data.result) {
        setChatCallList((prev) =>
          prev.map((r) =>
            String(r._id) === waitlistId ? { ...r, tagRecord: data.result } : r
          )
        );
        return data.result;
      }
    } catch { /* silent */ }
    return null;
  };

  const handleSaveTag = async (tagType, value) => {
    setTagModal((m) => ({ ...m, [`saving${tagType === "issueTag" ? "Issue" : tagType === "statusTag" ? "Status" : "Category"}`]: true }));
    try {
      const { data } = await saveCompletedChatCallTagApi({
        waitlistId:   tagModal.waitlistId,
        userId:       tagModal.userId,
        consultantId: tagModal.consultantId,
        tagType,
        value,
      });
      if (data?.code === 200) {
        toastMessage("Tag saved", "success", "tag-save");
        const updated = data.result;
        setTagModal((m) => ({
          ...m,
          issueTag:   updated.issueTag,
          statusTag:  updated.statusTag,
          categoryTag: updated.categoryTag,
          tagHistory: updated.tagHistory || [],
          evidence:   updated.evidence   || [],
          savingIssue: false, savingStatus: false, savingCategory: false,
        }));
        setChatCallList((prev) =>
          prev.map((r) => String(r._id) === tagModal.waitlistId ? { ...r, tagRecord: updated } : r)
        );
      } else {
        toastMessage(data?.message || "Failed to save tag", "error", "tag-err");
      }
    } catch {
      toastMessage("Error saving tag", "error", "tag-err");
    } finally {
      setTagModal((m) => ({ ...m, savingIssue: false, savingStatus: false, savingCategory: false }));
    }
  };

  const handleRemoveTag = async (tagType) => {
    setTagModal((m) => ({ ...m, [`saving${tagType === "issueTag" ? "Issue" : tagType === "statusTag" ? "Status" : "Category"}`]: true }));
    try {
      const { data } = await removeCompletedChatCallTagApi(tagModal.waitlistId, tagType);
      if (data?.code === 200) {
        toastMessage("Tag removed", "success", "tag-remove");
        const updated = data.result;
        setTagModal((m) => ({
          ...m,
          issueTag:   updated.issueTag  || null,
          statusTag:  updated.statusTag || null,
          categoryTag: updated.categoryTag || null,
          tagHistory: updated.tagHistory || [],
          evidence:   updated.evidence  || [],
          savingIssue: false, savingStatus: false, savingCategory: false,
        }));
        setChatCallList((prev) =>
          prev.map((r) => String(r._id) === tagModal.waitlistId ? { ...r, tagRecord: updated } : r)
        );
      } else {
        toastMessage(data?.message || "Failed to remove tag", "error", "tag-rem-err");
      }
    } catch {
      toastMessage("Error removing tag", "error", "tag-rem-err");
    } finally {
      setTagModal((m) => ({ ...m, savingIssue: false, savingStatus: false, savingCategory: false }));
    }
  };

  // ── Evidence helpers ───────────────────────────────────────────────────────
  const openEvidenceModal = (row) => {
    setEvidenceModal({
      open: true,
      waitlistId:   String(row._id),
      userId:       row.userObjectId ? String(row.userObjectId) : null,
      consultantId: null,
      evidence:     row.tagRecord?.evidence || [],
      noteText:     "",
      evidenceType: "whatsapp",
      uploading:    false,
      selectedFiles: [],
      audioFile:    null,
    });
  };

  const handleAddNoteEvidence = async () => {
    if (!evidenceModal.noteText.trim()) {
      toastMessage("Please enter a note", "error", "ev-note");
      return;
    }
    setEvidenceModal((m) => ({ ...m, uploading: true }));
    try {
      const { data } = await addCompletedChatCallNoteEvidenceApi({
        waitlistId:   evidenceModal.waitlistId,
        userId:       evidenceModal.userId,
        evidenceType: evidenceModal.evidenceType,
        note:         evidenceModal.noteText.trim(),
      });
      if (data?.code === 200) {
        toastMessage("Evidence saved", "success", "ev-save");
        setEvidenceModal((m) => ({
          ...m, evidence: data.result?.evidence || m.evidence, noteText: "", uploading: false,
        }));
        setChatCallList((prev) =>
          prev.map((r) => String(r._id) === evidenceModal.waitlistId ? { ...r, tagRecord: data.result } : r)
        );
      } else {
        toastMessage(data?.message || "Failed", "error", "ev-err");
      }
    } catch {
      toastMessage("Error saving evidence", "error", "ev-err");
    } finally {
      setEvidenceModal((m) => ({ ...m, uploading: false }));
    }
  };

  const handleAddScreenshotEvidence = async () => {
    if (!evidenceModal.selectedFiles.length) {
      toastMessage("Please select screenshot(s)", "error", "ev-file");
      return;
    }
    setEvidenceModal((m) => ({ ...m, uploading: true }));
    try {
      const formData = new FormData();
      formData.append("waitlistId", evidenceModal.waitlistId);
      if (evidenceModal.userId) formData.append("userId", evidenceModal.userId);
      formData.append("evidenceType", "screenshot");
      evidenceModal.selectedFiles.forEach((f) => formData.append("screenshots", f));
      const { data } = await addCompletedChatCallEvidenceApi(formData);
      if (data?.code === 200) {
        toastMessage("Screenshot(s) uploaded", "success", "ev-img");
        setEvidenceModal((m) => ({
          ...m, evidence: data.result?.evidence || m.evidence, selectedFiles: [], uploading: false,
        }));
        setChatCallList((prev) =>
          prev.map((r) => String(r._id) === evidenceModal.waitlistId ? { ...r, tagRecord: data.result } : r)
        );
      } else {
        toastMessage(data?.message || "Upload failed", "error", "ev-img-err");
      }
    } catch {
      toastMessage("Error uploading", "error", "ev-img-err");
    } finally {
      setEvidenceModal((m) => ({ ...m, uploading: false }));
    }
  };

  const handleAddAudioEvidence = async () => {
    if (!evidenceModal.audioFile) return;
    setEvidenceModal((m) => ({ ...m, uploading: true }));
    try {
      const formData = new FormData();
      formData.append("waitlistId", evidenceModal.waitlistId);
      if (evidenceModal.userId) formData.append("userId", evidenceModal.userId);
      formData.append("evidenceType", "phone_call");
      formData.append("audio", evidenceModal.audioFile);
      const { data } = await addCompletedChatCallEvidenceApi(formData);
      if (data?.code === 200) {
        toastMessage("Audio uploaded", "success", "ev-audio");
        setEvidenceModal((m) => ({ ...m, evidence: data.result?.evidence || m.evidence, audioFile: null, uploading: false }));
        setChatCallList((prev) =>
          prev.map((r) => String(r._id) === evidenceModal.waitlistId ? { ...r, tagRecord: data.result } : r)
        );
      } else {
        toastMessage(data?.message || "Upload failed", "error", "ev-audio-err");
      }
    } catch {
      toastMessage("Error uploading audio", "error", "ev-audio-err");
    } finally {
      setEvidenceModal((m) => ({ ...m, uploading: false }));
    }
  };

  const handleDeleteEvidence = async (evidenceId) => {
    try {
      const { data } = await deleteCompletedChatCallEvidenceApi(evidenceModal.waitlistId, evidenceId);
      if (data?.code === 200) {
        toastMessage("Evidence removed", "success", "ev-del");
        setEvidenceModal((m) => ({ ...m, evidence: data.result?.evidence || [] }));
        setChatCallList((prev) =>
          prev.map((r) => String(r._id) === evidenceModal.waitlistId ? { ...r, tagRecord: data.result } : r)
        );
      }
    } catch {
      toastMessage("Error removing evidence", "error", "ev-del-err");
    }
  };

  const today            = new Date().toISOString().split("T")[0];
  const totalAffected    = Object.values(problemStats).reduce((a, b) => a + b, 0);
  const allStatsLoaded   = PROBLEM_CATEGORIES.every((p) => problemStats[p.id] != null);

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">

          {/* ── Header ── */}
          <div className="cp-header">
            <h4 className="Title">Customer Problem Identification</h4>
            <div className="cp-header-controls">
              <select className="form-control" style={{ minWidth: 135, width: 'auto' }} value={selectedPreset} onChange={handleDateSelectChange}>
                <option value="">All Time</option>
                {DATE_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
                <option value="custom">Custom Range…</option>
              </select>
              {allStatsLoaded && (
                <div className="cp-total-badge">{totalAffected.toLocaleString()} affected</div>
              )}
            </div>
          </div>

          {/* ── Custom Range Bar ── */}
          {showCustomRange && (
            <div className="cp-custom-range-bar">
              <span className="cp-crb-label">Custom Range:</span>
              <div className="cp-crb-field">
                <label>From</label>
                <input type="date" className="form-control" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} max={rangeEnd || today} />
              </div>
              <span className="cp-crb-sep">—</span>
              <div className="cp-crb-field">
                <label>To</label>
                <input type="date" className="form-control" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} min={rangeStart} max={today} />
              </div>
              <button className="cp-apply-btn" onClick={applyCustomRange} disabled={!rangeStart && !rangeEnd}>Apply</button>
              <button className="cp-cancel-btn" onClick={() => { setShowCustomRange(false); setSelectedPreset("today"); setDateFilterLabel("Today"); triggerStatsReload({ timeRange: "today" }); }}>Cancel</button>
            </div>
          )}

          {/* ── Section bar ── */}
          <div className="cp-section-bar">
            <span className="cp-section-bar__text">
              Problem categories
              {dateFilterLabel && <span className="cp-section-bar__hint"> — {dateFilterLabel}</span>}
            </span>
            {selectedProblemId && (
              <button className="cp-clear-sel-btn" onClick={handleClearSelection}>× Clear Selection</button>
            )}
          </div>

          {/* ── Cards grid ── */}
          <div className="cp-cards-grid">
            {PROBLEM_CATEGORIES.map((p) => {
              const isSelected = selectedProblemId === p.id;
              const count      = problemStats[p.id];
              const toneClass  = `cp-card--tone-${((p.number - 1) % 5)}`;
              return (
                <div
                  key={p.id}
                  className={`cp-card ${toneClass} ${isSelected ? "cp-card--active" : ""}`}
                  onClick={() => handleSelectProblem(p.id)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleSelectProblem(p.id)}
                >
                  <div className="cp-card__left">
                    <div className="cp-card__body">
                      <span className="cp-card__num">#{p.number}</span>
                      <h6 className="cp-card__title">{p.title}</h6>
                      <p className="cp-card__desc">{p.description}</p>
                    </div>
                  </div>
                  <div className="cp-card__right">
                    {statsLoading[p.id] ? (
                      <LinearProgress
                        sx={{
                          width: 44,
                          height: 3,
                          borderRadius: 4,
                          backgroundColor: "#f2f4f7",
                          "& .MuiLinearProgress-bar": { backgroundColor: "#98a2b3" },
                        }}
                      />
                    ) : (
                      <span className="cp-card__count">{count != null ? count.toLocaleString() : "—"}</span>
                    )}
                    <span className={`cp-card__pill ${isSelected ? "active" : ""}`}>
                      {isSelected ? "Selected" : "View"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── User table ── */}
          {selectedProblem ? (
            <div ref={tableRef} className="cp-table-wrap">

              {/* Search bar */}
              <div className="cp-topbar">
                <div className="cp-search-box">
                  <IoSearch className="cp-si" />
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search by name, email, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && <IoCloseCircle className="cp-sc" onClick={() => setSearch("")} />}
                </div>
                <select className="form-control" style={{ width: 'auto' }} value={pageLimit} onChange={handlePageLimitChange}>
                  {[10, 25, 50, 100].map((v) => <option key={v} value={v}>{v} / page</option>)}
                </select>
                {selectedProblemId === "completedChatCall" && (
                  <>
                    <select className="form-control" style={{ width: "auto", minWidth: 140 }}
                      value={durationFilter}
                      onChange={(e) => { setDurationFilter(e.target.value); searchParam.delete("page"); setSearchParam(searchParam); }}>
                      {DURATION_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                    <select className="form-control" style={{ width: "auto", minWidth: 130 }}
                      value={issueTagFilter}
                      onChange={(e) => { setIssueTagFilter(e.target.value); searchParam.delete("page"); setSearchParam(searchParam); }}>
                      <option value="">All: Issue Tag</option>
                      <option value="issue">Issue</option>
                      <option value="no_issue">No Issue</option>
                    </select>
                    <select className="form-control" style={{ width: "auto", minWidth: 150 }}
                      value={statusTagFilter}
                      onChange={(e) => { setStatusTagFilter(e.target.value); searchParam.delete("page"); setSearchParam(searchParam); }}>
                      <option value="">All: Status Tag</option>
                      <option value="in_progress">In Progress</option>
                      <option value="solved">Solved</option>
                      <option value="not_solved">Not Solved</option>
                    </select>
                    <select className="form-control" style={{ width: "auto", minWidth: 155 }}
                      value={categoryTagFilter}
                      onChange={(e) => { setCategoryTagFilter(e.target.value); searchParam.delete("page"); setSearchParam(searchParam); }}>
                      <option value="">All: Tag 3</option>
                      <option value="technical">Technical</option>
                      <option value="consultancy">Consultancy</option>
                    </select>
                    {/* Specific date-time filter */}
                    {/* <div className="cc-dt-filter">
                      <label className="cc-dt-label">From</label>
                      <input type="date" className="form-control cc-dt-input" value={startDateTime}
                        onChange={(e) => { setStartDateTime(e.target.value); searchParam.delete("page"); setSearchParam(searchParam); }} />
                      <label className="cc-dt-label">To</label>
                      <input type="date" className="form-control cc-dt-input" value={endDateTime}
                        onChange={(e) => { setEndDateTime(e.target.value); searchParam.delete("page"); setSearchParam(searchParam); }} />
                      {(startDateTime || endDateTime) && (
                        <button className="cc-dt-clear" onClick={() => { setStartDateTime(""); setEndDateTime(""); }}>×</button>
                      )}
                    </div> */}
                  </>
                )}
                {selectedProblemId === "rechargeNotConsulted" && (
                  <select
                    className="form-control"
                    style={{ width: "auto", minWidth: 160 }}
                    value={pendingSinceBucket}
                    onChange={(e) => {
                      setPendingSinceBucket(e.target.value);
                      searchParam.delete("page");
                      setSearchParam(searchParam);
                    }}
                  >
                    {PENDING_SINCE_BUCKETS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
                <button className="cp-search-btn"
                  onClick={selectedProblemId === "completedChatCall" ? fetchChatCallList : fetchUserList}>
                  {selectedProblemId === "completedChatCall" ? "Apply" : "Search"}
                </button>
              </div>

              {selectedProblemId === "completedChatCall" ? (
                chatCallTotal > 0 && (
                  <p className="cp-results-line">
                    Showing {Math.min((activePage - 1) * pageLimit + 1, chatCallTotal)}–{Math.min(activePage * pageLimit, chatCallTotal)} of <strong>{chatCallTotal.toLocaleString()}</strong> consultations
                    {dateFilterLabel && <em> · {dateFilterLabel}</em>}
                  </p>
                )
              ) : (
                total > 0 && (
                  <p className="cp-results-line">
                    Showing {Math.min((activePage - 1) * pageLimit + 1, total)}–{Math.min(activePage * pageLimit, total)} of <strong>{total.toLocaleString()}</strong>{" "}
                    users
                    {dateFilterLabel && <em> · {dateFilterLabel}</em>}
                  </p>
                )
              )}

              {/* ── Summary Stats Bar ── */}
              {selectedProblemId === "completedChatCall" && (
                <div className="cc-summary-bar">
                  {summaryLoading ? (
                    <span className="cc-summary-loading">Loading stats…</span>
                  ) : summaryStats ? (
                    <>
                      <div className="cc-stat-pill cc-stat-total">
                        <span className="cc-stat-num">{summaryStats.total ?? 0}</span>
                        <span className="cc-stat-lbl">Total</span>
                      </div>
                      <div className="cc-stat-pill cc-stat-solved">
                        <span className="cc-stat-num">{summaryStats.solved ?? 0}</span>
                        <span className="cc-stat-lbl">Solved</span>
                      </div>
                      <div className="cc-stat-pill cc-stat-inprogress">
                        <span className="cc-stat-num">{summaryStats.inProgress ?? 0}</span>
                        <span className="cc-stat-lbl">In Progress</span>
                      </div>
                      <div className="cc-stat-pill cc-stat-notsolved">
                        <span className="cc-stat-num">{summaryStats.notSolved ?? 0}</span>
                        <span className="cc-stat-lbl">Not Solved</span>
                      </div>
                      <div className="cc-stat-divider" />
                      <div className="cc-stat-pill cc-stat-issue">
                        <span className="cc-stat-num">{summaryStats.issue ?? 0}</span>
                        <span className="cc-stat-lbl">Issue</span>
                      </div>
                      <div className="cc-stat-pill cc-stat-noissue">
                        <span className="cc-stat-num">{summaryStats.noIssue ?? 0}</span>
                        <span className="cc-stat-lbl">No Issue</span>
                      </div>
                      <div className="cc-stat-divider" />
                      <div className="cc-stat-pill cc-stat-technical">
                        <span className="cc-stat-num">{summaryStats.technical ?? 0}</span>
                        <span className="cc-stat-lbl">Technical</span>
                      </div>
                      <div className="cc-stat-pill cc-stat-consultancy">
                        <span className="cc-stat-num">{summaryStats.consultancy ?? 0}</span>
                        <span className="cc-stat-lbl">Consultancy</span>
                      </div>
                      <div className="cc-stat-pill cc-stat-untagged">
                        <span className="cc-stat-num">{summaryStats.untaggedStatus ?? 0}</span>
                        <span className="cc-stat-lbl">Untagged (Status)</span>
                      </div>
                      {/* <div className="cc-stat-pill cc-stat-untagged">
                        <span className="cc-stat-num">{summaryStats.untaggedCategory ?? 0}</span>
                        <span className="cc-stat-lbl">Untagged (Tag 3)</span>
                      </div> */}
                    </>
                  ) : null}
                </div>
              )}

              {/* ── Completed Chat Call table ── */}
              {selectedProblemId === "completedChatCall" && (
                <div className="content-table cp-tbl cp-cc-tbl">
                  <table>
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Date & Time</th>
                        <th>User ID</th>
                        <th>User Name</th>
                        <th>User Mobile</th>
                        {/* <th>Mail ID</th>
                        <th>User Age</th> */}
                        <th>User Gender</th>
                        <th>Mode</th>
                        <th>Duration</th>
                        <th>Amount Paid</th>
                        <th>Consultant Name</th>
                        <th>Consultant Mobile</th>
                        {/* <th>Consultant Age</th> */}
                        <th>Consultant Gender</th>
                        {/* <th>Which Consultation</th> */}
                        <th>Total Consults</th>
                        <th>Chat History</th>
                        <th>Session Logs</th>
                        <th>Network Logs</th>
                        <th>Tag 1 — Issue</th>
                        <th>Tag 2 — Status</th>
                        <th>Tag 3 - Category</th>
                        <th>Evidence</th>
                        <th>History</th>
                        <th>Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chatCallList.length > 0 ? chatCallList.map((row, idx) => (
                        <tr key={String(row._id)}>
                          <td>{findSerialNumber(idx, activePage, pageLimit)}</td>
                          <td className="cp-nowrap">{dateFormat(row.consultationDate) || "—"}</td>
                          <td>
                            {row.userObjectId ? (
                              <Link to={`${USER_DETAIL}/${row.userObjectId}`} className="cp-uid-link">
                                {row.userId || "—"}
                              </Link>
                            ) : <span className="cp-uid">{row.userId || "—"}</span>}
                          </td>
                          <td>
                            <div className="cp-user-cell">
                              <img className="cp-avatar" src={row.profileImage || profileImage} alt={row.userName || ""} />
                              <span className="cp-uname">{row.userName || "—"}</span>
                            </div>
                          </td>
                          <td>{row.userMobile ? <a href={`tel:${row.userMobile}`} className="cp-phone">{row.userMobile}</a> : "—"}</td>
                          {/* <td>{row.userEmail || "—"}</td> */}
                          {/* <td>{calcAge(row.userDob)}</td> */}
                          <td>{CapitalizeFirstWord(row.userGender || "") || "—"}</td>
                          <td><span className="cp-badge-gray">{CapitalizeFirstWord(row.consultationMode || "") || "—"}</span></td>
                          <td><span className="cp-badge-warn">{formatDurationSecs(row.durationSeconds.toFixed(0))}</span></td>
                          <td><span className="cp-wallet">₹{typeof row.Amount === "number" ? row.Amount.toFixed(2) : "—"}</span></td>
                          {/* <td>{row.consultantName || "—"}</td> */}
                           <td> <Link target="_blank"
                                                                                                              to={`/consultant-management/consultant-detail/${row.rawConsultantId}`}
                                                                                                            >{row?.consultantName || "---"}</Link></td>
                          <td>{row.consultantMobile ? <a href={`tel:${row.consultantMobile}`} className="cp-phone">{row.consultantMobile}</a> : "—"}</td>
                          {/* <td>{calcAge(row.consultantDob)}</td> */}
                          <td>{CapitalizeFirstWord(row.consultantGender || "") || "—"}</td>
                          {/* <td>{row.whichConsultation || "—"}</td> */}
                          <td><span className="cp-badge-gray">{row.totalConsultations ?? "—"}</span></td>
                          <td>
                            <button className="cp-log-open-btn" onClick={() => openChatHistory(row)}>
                              View Chat
                            </button>
                          </td>
                          <td>
                            <button className="cp-log-open-btn" onClick={() => handleShowOrderLogs(row)}>
                              Session Logs
                            </button>
                          </td>
                          <td>
                            <button
                              className="cp-log-open-btn"
                              onClick={() => handleViewNetworkLogs(String(row._id))}
                            >
                              Network Logs
                            </button>
                          </td>
                          {/* Tag 1 */}
                          <td>
                            {row.tagRecord?.issueTag?.value ? (
                              <div className="cc-tag-cell">
                                <span className="cc-tag-badge" style={{ background: issueTagColor(row.tagRecord.issueTag.value) }}>
                                  {row.tagRecord.issueTag.value === "issue" ? "Issue" : "No Issue"}
                                </span>
                                {/* <span className="cc-tag-time">{dateFormat(row.tagRecord.issueTag.setAt)}</span>
                                <span className="cc-tag-by">{row.tagRecord.issueTag.setByName}</span> */}
                              </div>
                            ) : <span className="cc-tag-none">—</span>}
                          </td>
                          {/* Tag 2 */}
                          <td>
                            {row.tagRecord?.statusTag?.value ? (
                              <div className="cc-tag-cell">
                                <span className="cc-tag-badge" style={{ background: statusTagColor(row.tagRecord.statusTag.value) }}>
                                  {statusTagLabel(row.tagRecord.statusTag.value)}
                                </span>
                                {row.tagRecord.statusTag.isAutomatic && <span className="cc-tag-auto">Auto</span>}
                                {/* <span className="cc-tag-time">{dateFormat(row.tagRecord.statusTag.setAt)}</span>
                                <span className="cc-tag-by">{row.tagRecord.statusTag.setByName}</span> */}
                              </div>
                            ) : <span className="cc-tag-none">—</span>}
                          </td>
                          {/* Tag 3 */}
                          <td>
                            {row.tagRecord?.categoryTag?.value ? (
                              <div className="cc-tag-cell">
                                <span className="cc-tag-badge" style={{ background: categoryTagColor(row.tagRecord.categoryTag.value) }}>
                                  {categoryTagLabel(row.tagRecord.categoryTag.value)}
                                </span>
                              </div>
                            ) : <span className="cc-tag-none">—</span>}
                          </td>
                          {/* Evidence */}
                          <td>
                            <button className="cp-log-open-btn cc-ev-btn" onClick={() => openEvidenceModal(row)}>
                              <AttachFileIcon sx={{ fontSize: 13 }} /> {row.tagRecord?.evidence?.length || 0}
                            </button>
                          </td>
                          {/* History */}
                          <td>
                            <button className="cp-log-open-btn" onClick={() => setHistoryModal({ open: true, history: row.tagRecord?.tagHistory || [], title: row.userName || String(row._id).slice(-6) })}>
                              <HistoryIcon sx={{ fontSize: 13 }} /> {row.tagRecord?.tagHistory?.length || 0}
                            </button>
                          </td>
                          {/* Manage */}
                          <td>
                            <button className="cc-manage-btn" onClick={() => openTagModal(row)}>Manage Tags</button>
                          </td>
                        </tr>
                      )) : (
                        <NotFound cols={22} msg="No chat consultations found for this filter." icon={NOT_FOUND} loader={loader} />
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Table */}
              {selectedProblemId !== "completedChatCall" && (
              <div className="content-table cp-tbl">
                <table>
                  <thead>
                    {selectedProblemId === "rechargeUnsuccessful" ? (
                      <tr>
                        <th>S.No</th>
                        <th>User</th>
                        <th>User ID</th>
                        <th>Phone</th>
                        <th>Registered On</th>
                        <th>Failed</th>
                        <th>Initiated</th>
                        <th>Success</th>
                        <th>Attempt log</th>
                        <th className="cp-remark-th">Remark</th>
                      </tr>
                    ) : selectedProblemId === "rechargeNotConsulted" ? (
                      <tr>
                        <th>S.No</th>
                        <th>User</th>
                        <th>User ID</th>
                        <th>Phone</th>
                        <th>Registered On</th>
                        <th>Recharge at</th>
                        <th>Payment method</th>
                        <th>Pending since</th>
                        <th>History</th>
                        <th className="cp-remark-th">Remark</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>S.No</th>
                        <th>User</th>
                        <th>User ID</th>
                        <th>Phone</th>
                        <th>Registered On</th>
                        <th>Attempts</th>
                        <th>Completed</th>
                        <th>Wallet (₹)</th>
                        <th className="cp-remark-th">Remark / Action Taken</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {userList.length > 0 ? (
                      selectedProblemId === "rechargeUnsuccessful"
                        ? userList.map((u, idx) => (
                          (() => {
                            const statusCounts = getRechargeStatusCounts(u);
                            const latestSuccess = getLatestSuccessMeta(u);
                            return (
                          <tr key={String(u._id)}>
                            <td>{findSerialNumber(idx, activePage, pageLimit)}</td>
                            <td>
                              <div className="cp-user-cell">
                                <img className="cp-avatar" src={u.profileImage || profileImage} alt={u.name || ""} />
                                <div>
                                  <span className="cp-uname">{u.name || "---"}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              {u.userId ? (
                                <Link to={`${USER_DETAIL}/${u._id}`} className="cp-uid-link">
                                  {u.userId}
                                </Link>
                              ) : (
                                <span className="cp-uid">---</span>
                              )}
                            </td>
                            <td>
                              {u.phone ? <a href={`tel:${u.phone}`} className="cp-phone">{u.phone}</a> : "---"}
                            </td>
                            <td>{dateFormat(u.createdAt) || "---"}</td>
                            <td><span className="cp-badge-gray">{statusCounts.failed}</span></td>
                            <td><span className="cp-badge-warn">{statusCounts.initiated}</span></td>
                            <td>
                              <span className="cp-badge-green">{statusCounts.success}</span>
                              {latestSuccess?.at ? (
                                <div className="cp-muted" style={{ marginTop: 4 }}>
                                  {latestSuccess.status} on {dateFormat(latestSuccess.at)}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              {u.rechargeAttemptLog?.length ? (
                                <button
                                  type="button"
                                  className="cp-log-open-btn"
                                  onClick={() =>
                                    setAttemptLogModal({
                                      open: true,
                                      title: u.name || u.userId || "User",
                                      heading: "Recharge attempt log",
                                      log: u.rechargeAttemptLog,
                                    })
                                  }
                                >
                                  View log ({u.rechargeAttemptLog.length})
                                </button>
                              ) : (
                                "---"
                              )}
                            </td>
                            <td className="cp-remark-td">
                              <RemarkCell
                                userId={u._id}
                                problemType={selectedProblemId}
                                initialRemark={remarks[remarkKeyForRow(u)] ?? ""}
                                onSaved={handleRemarkSaved}
                              />
                            </td>
                          </tr>
                            );
                          })()
                        ))
                        : selectedProblemId === "rechargeNotConsulted"
                          ? userList.map((u, idx) => (
                            <tr key={`${String(u._id)}-${u.rechargeTransactionId || idx}`}>
                              <td>{findSerialNumber(idx, activePage, pageLimit)}</td>
                              <td>
                                <div className="cp-user-cell">
                                  <img className="cp-avatar" src={u.profileImage || profileImage} alt={u.name || ""} />
                                  <div>
                                    <span className="cp-uname">{u.name || "---"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {u.userId ? (
                                  <Link to={`${USER_DETAIL}/${u._id}`} className="cp-uid-link">
                                    {u.userId}
                                  </Link>
                                ) : (
                                  <span className="cp-uid">---</span>
                                )}
                              </td>
                              <td>
                                {u.phone ? <a href={`tel:${u.phone}`} className="cp-phone">{u.phone}</a> : "---"}
                              </td>
                              <td>{dateFormat(u.createdAt) || "---"}</td>
                              <td>{dateFormat(u.rechargeAt) || "---"}</td>
                              <td>
                                <span className="cp-badge-gray">
                                  {normalizePaymentMethod(getLatestRechargeEntry(u)?.paymentMethod)}
                                </span>
                              </td>
                              <td><span className="cp-badge-warn">{formatElapsedSince(u.elapsedMs)}</span></td>
                              <td>
                                {(u.rechargeAt || (Array.isArray(u.rechargeHistory) && u.rechargeHistory.length > 0)) ? (
                                  <button
                                    type="button"
                                    className="cp-log-open-btn"
                                    onClick={() => openRechargeConsultHistory(u)}
                                  >
                                    View history
                                  </button>
                                ) : (
                                  "---"
                                )}
                              </td>
                              <td className="cp-remark-td">
                                <RemarkCell
                                  userId={u._id}
                                  problemType={selectedProblemId}
                                  contextKey={u.rechargeTransactionId}
                                  initialRemark={remarks[remarkKeyForRow(u)] ?? ""}
                                  onSaved={handleRemarkSaved}
                                />
                              </td>
                            </tr>
                          ))
                          : userList.map((u, idx) => (
                            <tr key={u._id || idx}>
                              <td>{findSerialNumber(idx, activePage, pageLimit)}</td>
                              <td>
                                <div className="cp-user-cell">
                                  <img className="cp-avatar" src={u.profileImage || profileImage} alt={u.name || ""} />
                                  <div>
                                    <span className="cp-uname">{u.name || "---"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {u.userId ? (
                                  <Link to={`${USER_DETAIL}/${u._id}`} className="cp-uid-link">
                                    {u.userId}
                                  </Link>
                                ) : (
                                  <span className="cp-uid">---</span>
                                )}
                              </td>
                              <td>
                                {u.phone
                                  ? <a href={`tel:${u.phone}`} className="cp-phone">{u.phone}</a>
                                  : "---"}
                              </td>
                              <td>{dateFormat(u.createdAt) || "---"}</td>
                              <td><span className="cp-badge-gray">{u.totalAttempts ?? 0}</span></td>
                              <td><span className="cp-badge-green">{u.totalCompleted ?? 0}</span></td>
                              <td><span className="cp-wallet">₹{typeof u.wallet === "number" ? u.wallet.toFixed(2) : "---"}</span></td>
                              <td className="cp-remark-td">
                                <RemarkCell
                                  userId={u._id}
                                  problemType={selectedProblemId}
                                  initialRemark={remarks[remarkKeyForRow(u)] ?? ""}
                                  onSaved={handleRemarkSaved}
                                />
                              </td>
                            </tr>
                          ))
                    ) : (
                      <NotFound
                        cols={selectedProblemId === "rechargeUnsuccessful" ? 10 : selectedProblemId === "rechargeNotConsulted" ? 10 : 9}
                        msg="No rows found for this problem category."
                        icon={NOT_FOUND}
                        loader={loader}
                      />
                    )}
                  </tbody>
                </table>
              </div>
              )}

              {selectedProblemId === "completedChatCall" ? (
                chatCallTotal > pageLimit && (
                  <div className="cp-pagination-bar">
                    <Typography variant="body2" color="textSecondary" />
                    <div className="Pagination">
                      <Pagination
                        activePage={activePage}
                        itemsCountPerPage={pageLimit}
                        totalItemsCount={chatCallTotal}
                        pageRangeDisplayed={5}
                        onChange={handlePageChange}
                        hideDisabled
                      />
                    </div>
                  </div>
                )
              ) : (
                total > pageLimit && (
                  <div className="cp-pagination-bar">
                    <Typography variant="body2" color="textSecondary" />
                    <div className="Pagination">
                      <Pagination
                        activePage={activePage}
                        itemsCountPerPage={pageLimit}
                        totalItemsCount={total}
                        pageRangeDisplayed={5}
                        onChange={handlePageChange}
                        hideDisabled
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="cp-empty-hint">
              <IoSearch className="cp-empty-hint__ico" />
              <span>Select a problem category above to view affected customers</span>
            </div>
          )}

        </div>
      </div>

      <Dialog
        open={attemptLogModal.open}
        onClose={() => setAttemptLogModal((m) => ({ ...m, open: false }))}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className="cp-log-dialog-title">
          {attemptLogModal.heading || "Recharge attempt log"}
          {attemptLogModal.title ? (
            <span className="cp-log-dialog-sub"> — {attemptLogModal.title}</span>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          {attemptLogModal.log?.length ? (
            <div className="cp-log-modal-table">
              <div className="cp-log-modal-head">
                <span>{attemptLogModal.methodHeader || "Activity / Payment method"}</span>
                <span>Date & Time</span>
                <span>Status</span>
                <span>Details</span>
              </div>
              <div className="cp-log-modal-body">
                {attemptLogModal.log.map((e, i) => (
                  (() => {
                    const attemptAmount = resolveAttemptAmount(e);
                    return (
                      <div key={i} className="cp-log-modal-row">
                        <span className="cp-log-type-chip">
                          {normalizePaymentMethod(
                            e.paymentMethod || e.method || e.pgType || e.gateway || e.provider || e.type
                          )}
                        </span>
                        <span className="cp-log-date">{dateFormat(e.at) || "—"}</span>
                        <span className={`cp-log-status cp-log-status--${txnStatusToneClass(e.status)}`}>
                          {formatTxnStatus(e.status)}
                        </span>
                        <span className="cp-log-meta">
                          {attemptAmount != null ? (
                            <span className="cp-log-meta__line">
                              Recharge attempt amount:{" "}
                              <strong>{`₹${attemptAmount.toFixed(2)}`}</strong>
                            </span>
                          ) : null}
                          {e.orderId ? (
                            <span className="cp-log-meta__line">
                              Order ID: <strong>{e.orderId}</strong>
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })()
                ))}
              </div>
            </div>
          ) : (
            <p className="cp-muted">No events in this period.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setAttemptLogModal((m) => ({ ...m, open: false }))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Order-history style consultation logs (from waitlist.logs) ── */}
      <OrderLogModal
        open={orderLogsModal.open}
        onClose={() => setOrderLogsModal({ open: false, logs: [] })}
        logs={orderLogsModal.logs}
      />

      {/* ── Consultation Network Logs Modal ─────────────────────────────────── */}
      {logsModalOpen && (
        <>
          <style>{`
            .timeline-logs-modal { display: flex; gap: 36px; padding: 0 4px 0 0; min-width: 660px; }
            .timeline-col { flex: 1 1 0; min-width: 300px; background: #f7fafc; border-radius: 12px; box-shadow: 0 6px 38px #0001; display: flex; flex-direction: column; height: 480px; }
            .timeline-header { position: sticky; top: 0; background: #f7fafc; z-index: 2; padding: 18px 0 8px 0; text-align: center; font-weight: 600; font-size: 1.14em; color: #3854ab; border-bottom: 1px solid #ececec; }
            .timeline-header.consultant { color: #1e8c29; }
            .timeline-scroll { overflow-y: auto; padding: 0 10px 18px 10px; flex: 1 1 0; }
            .log-timeline-card { border: 1.5px solid #e5e7eb; background: #fff; border-radius: 9px; margin: 18px 0 10px 0; padding: 13px 15px; transition: box-shadow .13s; box-shadow: 0 3px 8px #ddd2; }
            .log-card-bad { border-color: #eb4949!important; background: #fff5f2!important; }
            .log-card-warning { border-color: #face6d!important; background: #fffceb!important; }
            .log-main-row { display: flex; align-items: center; gap: 22px; font-size: 16px; }
            .log-time { font-size: 14px; color: #444; width: 70px; }
            .log-dot { width: 10px; height: 10px; border-radius: 50%; background: #347fc6; display: inline-block; }
            .log-type { font-size: 13px; color: #717070; background: #e5e7eb; border-radius: 12px; padding: 0 10px; margin-left: 4px; }
            .log-section-label { font-size: 12px; color: #222; font-weight: 500; }
            .log-section-value { font-size: 14px; color: #333; font-weight: 400; }
            .log-green { color: #127b34; }
            .log-red { color: #b22020; }
            .log-card-actions { margin-top: 6px; }
            .log-more-btn { border: none; background: none; color: #257aff; font-size: 13px; cursor: pointer; margin-top: 2px; padding: 0; }
            .log-card-raw { background: #f4f7fa; border: 1px solid #dee3f0; border-radius: 6px; margin-top: 9px; font-family: 'fira mono', mono; font-size: 13px; color: #616174; white-space: pre-wrap; overflow-x: auto; padding: 10px; }
            @media (max-width: 820px) {
              .timeline-logs-modal { flex-direction: column; }
              .timeline-col { min-width: 0; }
            }
          `}</style>

          <ModalBox
            status={logsModalOpen}
            onClose={handleCloseLogsModal}
            closeIcon
            size="md"
            title="Network Logs"
            description=""
            custom={true}
          >
            {logsLoading ? (
              <div style={{ padding: "28px", textAlign: "center" }}>Loading logs...</div>
            ) : logsError ? (
              <div style={{ padding: "24px", color: "#f44336", textAlign: "center" }}>{logsError}</div>
            ) : (
              <div className="timeline-logs-modal">
                <div className="timeline-col">
                  <div className="timeline-header">User Session Logs</div>
                  <div className="timeline-scroll">
                    {userLogs.length === 0 ? (
                      <div style={{ color: "#888", textAlign: "center", marginTop: "65px" }}>No User Logs</div>
                    ) : (
                      userLogs.map((log, idx) => (
                        <LogCard key={log._id || idx} log={log} />
                      ))
                    )}
                  </div>
                </div>

                <div className="timeline-col">
                  <div className="timeline-header consultant">Consultant Session Logs</div>
                  <div className="timeline-scroll">
                    {consultantLogs.length === 0 ? (
                      <div style={{ color: "#888", textAlign: "center", marginTop: "65px" }}>No Consultant Logs</div>
                    ) : (
                      consultantLogs.map((log, idx) => (
                        <LogCard key={log._id || idx} log={log} />
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </ModalBox>
        </>
      )}

      {/* ── Tag Management Dialog ── */}
      <Dialog open={tagModal.open} onClose={() => setTagModal((m) => ({ ...m, open: false }))} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle className="cp-log-dialog-title">
          Manage Tags
          {tagModal.waitlistId && <span className="cp-log-dialog-sub"> — Consultation #{tagModal.waitlistId.slice(-6)}</span>}
        </DialogTitle>
        <DialogContent dividers>
          {/* Tag 1 — Issue / No Issue */}
          <div className="cc-modal-section">
            <h6 className="cc-modal-section-title">Tag 1 — Issue / No Issue</h6>
            {tagModal.issueTag?.value && (
              <div className="cc-current-tag">
                Current: <span className="cc-tag-badge" style={{ background: issueTagColor(tagModal.issueTag.value) }}>
                  {tagModal.issueTag.value === "issue" ? "Issue" : "No Issue"}
                </span>
                <span className="cc-tag-meta"> · {dateFormat(tagModal.issueTag.setAt)} · {tagModal.issueTag.setByName}</span>
              </div>
            )}
            <div className="cc-tag-btn-row">
              {ISSUE_TAG_OPTIONS.map((opt) => (
                <button key={opt.value} disabled={tagModal.savingIssue}
                  className={`cc-tag-action-btn ${tagModal.issueTag?.value === opt.value ? "cc-tag-action-btn--active" : ""}`}
                  style={{ borderColor: issueTagColor(opt.value), color: tagModal.issueTag?.value === opt.value ? "#fff" : issueTagColor(opt.value), background: tagModal.issueTag?.value === opt.value ? issueTagColor(opt.value) : "transparent" }}
                  onClick={() => tagModal.issueTag?.value === opt.value ? handleRemoveTag("issueTag") : handleSaveTag("issueTag", opt.value)}>
                  {tagModal.savingIssue ? "Saving…" : opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tag 2 — Solved / In-Progress */}
          <div className="cc-modal-section">
            <h6 className="cc-modal-section-title">Tag 2 — Status</h6>
            {tagModal.statusTag?.value && (
              <div className="cc-current-tag">
                Current: <span className="cc-tag-badge" style={{ background: statusTagColor(tagModal.statusTag.value) }}>
                  {statusTagLabel(tagModal.statusTag.value)}
                </span>
                {tagModal.statusTag.isAutomatic && <span className="cc-tag-auto"> (Auto)</span>}
                <span className="cc-tag-meta"> · {dateFormat(tagModal.statusTag.setAt)} · {tagModal.statusTag.setByName}</span>
              </div>
            )}
            <div className="cc-tag-btn-row">
              {STATUS_TAG_OPTIONS.map((opt) => (
                <button key={opt.value} disabled={tagModal.savingStatus}
                  className={`cc-tag-action-btn ${tagModal.statusTag?.value === opt.value ? "cc-tag-action-btn--active" : ""}`}
                  style={{ borderColor: statusTagColor(opt.value), color: tagModal.statusTag?.value === opt.value ? "#fff" : statusTagColor(opt.value), background: tagModal.statusTag?.value === opt.value ? statusTagColor(opt.value) : "transparent" }}
                  onClick={() => tagModal.statusTag?.value === opt.value ? handleRemoveTag("statusTag") : handleSaveTag("statusTag", opt.value)}>
                  {tagModal.savingStatus ? "Saving…" : opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tag 3 - Technical / Consultancy */}
          <div className="cc-modal-section">
            <h6 className="cc-modal-section-title">Tag 3 - Technical / Consultancy</h6>
            {tagModal.categoryTag?.value && (
              <div className="cc-current-tag">
                Current: <span className="cc-tag-badge" style={{ background: categoryTagColor(tagModal.categoryTag.value) }}>
                  {categoryTagLabel(tagModal.categoryTag.value)}
                </span>
                <span className="cc-tag-meta"> · {dateFormat(tagModal.categoryTag.setAt)} · {tagModal.categoryTag.setByName}</span>
              </div>
            )}
            <div className="cc-tag-btn-row">
              {CATEGORY_TAG_OPTIONS.map((opt) => (
                <button key={opt.value} disabled={tagModal.savingCategory}
                  className={`cc-tag-action-btn ${tagModal.categoryTag?.value === opt.value ? "cc-tag-action-btn--active" : ""}`}
                  style={{ borderColor: categoryTagColor(opt.value), color: tagModal.categoryTag?.value === opt.value ? "#fff" : categoryTagColor(opt.value), background: tagModal.categoryTag?.value === opt.value ? categoryTagColor(opt.value) : "transparent" }}
                  onClick={() => tagModal.categoryTag?.value === opt.value ? handleRemoveTag("categoryTag") : handleSaveTag("categoryTag", opt.value)}>
                  {tagModal.savingCategory ? "Saving…" : opt.label}
                </button>
              ))}
            </div>
          </div>

        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setTagModal((m) => ({ ...m, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Evidence Dialog ── */}
      <Dialog open={evidenceModal.open} onClose={() => setEvidenceModal((m) => ({ ...m, open: false }))} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle className="cp-log-dialog-title">
          Evidence
          {evidenceModal.waitlistId && <span className="cp-log-dialog-sub"> — #{evidenceModal.waitlistId.slice(-6)}</span>}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>

          {/* Existing evidence */}
          {evidenceModal.evidence?.length > 0 && (
            <div className="cc-ev-section">
              <div className="cc-ev-section-header">
                <span className="cc-ev-section-title">Attached Evidence</span>
                <span className="cc-ev-section-count">{evidenceModal.evidence.length} item{evidenceModal.evidence.length !== 1 ? "s" : ""}</span>
              </div>

              {/* Screenshot grid */}
              {evidenceModal.evidence.some((e) => e.evidenceType === "screenshot") && (
                <div className="cc-ev-screenshot-grid">
                  {evidenceModal.evidence.filter((ev) => ev.evidenceType === "screenshot").map((ev, i) => (
                    <div key={ev._id || `ss-${i}`} className="cc-ev-ss-card">
                      <a href={evImgSrc(ev.url)} target="_blank" rel="noreferrer" className="cc-ev-ss-img-wrap">
                        <img src={evImgSrc(ev.url)} alt={`screenshot-${i + 1}`} className="cc-ev-ss-img" />
                        <span className="cc-ev-ss-overlay">View</span>
                      </a>
                      <div className="cc-ev-ss-footer">
                        <span className="cc-ev-ss-meta">{dateFormat(ev.addedAt)} · {ev.addedByName}</span>
                        <button className="cc-ev-del-btn" onClick={() => handleDeleteEvidence(ev._id)}>
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Note / audio evidence list */}
              {evidenceModal.evidence.filter((ev) => ev.evidenceType !== "screenshot").map((ev, i) => (
                <div key={ev._id || `note-${i}`} className="cc-ev-note-card">
                  <div className="cc-ev-note-left">
                    <span className={`cc-ev-type-badge cc-ev-type-${ev.evidenceType}`}>
                      {ev.evidenceType === "whatsapp" ? "WhatsApp" : "Phone Call"}
                    </span>
                    {ev.evidenceType === "phone_call" && ev.url && (
                      <audio controls src={evImgSrc(ev.url)} className="cc-ev-audio-player" />
                    )}
                    {ev.note && <p className="cc-ev-note-text">{ev.note}</p>}
                    <span className="cc-ev-note-meta">{dateFormat(ev.addedAt)} · {ev.addedByName}</span>
                  </div>
                  <button className="cc-ev-del-btn" onClick={() => handleDeleteEvidence(ev._id)}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add evidence forms */}
          <div className="cc-ev-forms-wrap">
            {/* Note evidence */}
            <div className="cc-ev-form-card">
              <div className="cc-ev-form-header">Add Note Evidence</div>
              <div className="cc-ev-form-body">
                <select className="cc-ev-select" value={evidenceModal.evidenceType}
                  onChange={(e) => setEvidenceModal((m) => ({ ...m, evidenceType: e.target.value }))}>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="phone_call">Phone Call</option>
                </select>
                <textarea className="cc-ev-textarea" rows={3} placeholder="Describe what happened…"
                  value={evidenceModal.noteText}
                  onChange={(e) => setEvidenceModal((m) => ({ ...m, noteText: e.target.value }))} />
                <button className="cc-ev-save-btn" disabled={evidenceModal.uploading} onClick={handleAddNoteEvidence}>
                  {evidenceModal.uploading ? "Saving…" : "Save Note"}
                </button>
                {evidenceModal.evidenceType === "phone_call" && (
                  <div className="cc-ev-audio-section">
                    <span className="cc-ev-audio-label">Upload Call Recording (optional)</span>
                    <label className="cc-ev-file-label">
                      <input type="file" accept="audio/*" className="cc-ev-file-hidden"
                        onChange={(e) => setEvidenceModal((m) => ({ ...m, audioFile: e.target.files[0] || null }))} />
                      <span className="cc-ev-file-btn">
                        {evidenceModal.audioFile ? evidenceModal.audioFile.name : "Choose audio file…"}
                      </span>
                    </label>
                    {evidenceModal.audioFile && (
                      <audio controls src={URL.createObjectURL(evidenceModal.audioFile)} className="cc-ev-audio-preview" />
                    )}
                    <button className="cc-ev-save-btn" disabled={evidenceModal.uploading || !evidenceModal.audioFile}
                      onClick={handleAddAudioEvidence}>
                      {evidenceModal.uploading ? "Uploading…" : "Upload Recording"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Screenshot upload */}
            <div className="cc-ev-form-card">
              <div className="cc-ev-form-header">Attach Screenshots</div>
              <div className="cc-ev-form-body">
                <label className="cc-ev-file-label">
                  <input type="file" accept="image/*" multiple className="cc-ev-file-hidden"
                    onChange={(e) => setEvidenceModal((m) => ({ ...m, selectedFiles: Array.from(e.target.files) }))} />
                  <span className="cc-ev-file-btn">
                    {evidenceModal.selectedFiles?.length > 0
                      ? `${evidenceModal.selectedFiles.length} file(s) selected`
                      : "Choose images…"}
                  </span>
                </label>
                {evidenceModal.selectedFiles?.length > 0 && (
                  <div className="cc-ev-preview-row">
                    {evidenceModal.selectedFiles.map((f, i) => (
                      <img key={i} src={URL.createObjectURL(f)} alt={f.name} className="cc-ev-preview-thumb" />
                    ))}
                  </div>
                )}
                <button className="cc-ev-save-btn" disabled={evidenceModal.uploading} onClick={handleAddScreenshotEvidence}>
                  {evidenceModal.uploading ? "Uploading…" : "Upload Screenshots"}
                </button>
              </div>
            </div>
          </div>

        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setEvidenceModal((m) => ({ ...m, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Chat History Dialog ── */}
      <Dialog
        open={chatHistoryModal.open}
        onClose={() => setChatHistoryModal((m) => ({ ...m, open: false }))}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { height: "80vh", maxHeight: 680, display: "flex", flexDirection: "column", borderRadius: "12px", overflow: "hidden" } }}
      >
        {/* Header — "{userName}" + call window + × */}
        <div className="ch-header">
          <div className="ch-header-left">
            <span className="ch-title">{chatHistoryModal.row?.userName || "User"}</span>
            {chatHistoryModal.row && (() => {
              const startMs = new Date(chatHistoryModal.row.callStartTime || chatHistoryModal.row.consultationDate).getTime();
              const endMs   = startMs + (chatHistoryModal.row.durationSeconds || 0) * 1000;
              return (
                <div className="ch-call-window">
                  <span className="ch-call-label">Start:</span>
                  <span className="ch-call-time">{dateFormatWithTime(new Date(startMs))}</span>
                  <span className="ch-call-sep">→</span>
                  <span className="ch-call-label">End:</span>
                  <span className="ch-call-time">{dateFormatWithTime(new Date(endMs))}</span>
                </div>
              );
            })()}
          </div>
          <button className="ch-close-btn" onClick={() => setChatHistoryModal((m) => ({ ...m, open: false }))}>×</button>
        </div>

        {/* Scrollable messages body */}
        <div className="ch-body" ref={chatBodyRef}>
          {chatHistoryModal.recordingUrl && (
            <div style={{
              background: "#fff",
              border: "1px solid #e9ecef",
              borderRadius: "10px",
              padding: "14px",
              marginBottom: "14px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                <strong style={{ color: "#333", fontSize: 14 }}>Call Recording</strong>
                <span style={{ color: "#666", fontSize: 12 }}>
                  {chatHistoryModal.orderId ? `Order ID: ${chatHistoryModal.orderId}` : chatHistoryModal.consultationMode ? `Mode: ${chatHistoryModal.consultationMode}` : ""}
                </span>
              </div>
              <AudioPlayer source={chatHistoryModal.recordingUrl} />
            </div>
          )}

          {/* Load-more area at top */}
          {!chatHistoryModal.loading && chatHistoryModal.page < chatHistoryModal.totalPages && (
            <div className="ch-load-more-wrap">
              {chatHistoryModal.loadingMore ? (
                <div className="ch-loading-more">
                  <div className="ch-spinner" style={{ width: 14, height: 14 }} />
                  <span>Loading older messages…</span>
                </div>
              ) : (
                <button className="ch-load-more-btn" onClick={doLoadMoreChat}>
                  Load previous messages
                </button>
              )}
            </div>
          )}
          {chatHistoryModal.loading ? (
            <div className="ch-center">
              <div className="ch-spinner" />
              <span className="ch-loading-text">Loading messages…</span>
            </div>
          ) : chatHistoryModal.messages?.length > 0 ? (
            <>
              {chatHistoryModal.messages.map((msg, i) => {
                // Messages NOT from the user (= consultant) go on the RIGHT (pink), matching ChatBox
                const isConsultantSide = msg?.from?.id !== chatHistoryModal.userId;
                const isSystem = msg?.type === "html" && msg?.metaEditor?.html;
                const showDate = i === 0 || (
                  msg?.createdAt && chatHistoryModal.messages[i - 1]?.createdAt &&
                  new Date(msg.createdAt).toDateString() !== new Date(chatHistoryModal.messages[i - 1].createdAt).toDateString()
                );

                let content;
                if (msg?.type === "image" && msg?.metadata?.fileUrl) {
                  content = (
                    <a href={msg.metadata.fileUrl} target="_blank" rel="noreferrer" className="ch-img-link">
                      <img src={msg.metadata.fileUrl} alt={msg.metadata.filename || "image"} className="ch-msg-img" />
                    </a>
                  );
                } else if (msg?.type === "audio" && msg?.metadata?.fileUrl) {
                  content = <AudioPlayer source={msg.metadata.fileUrl} />;
                } else if (isSystem) {
                  content = <div dangerouslySetInnerHTML={{ __html: msg.metaEditor.html }} className="ch-msg-html" />;
                } else {
                  content = <div dangerouslySetInnerHTML={{ __html: msg?.content || msg?.message || "" }} />;
                }

                return (
                  <div key={String(msg._id || i)}>
                    {showDate && (
                      <div className="ch-date-sep-wrap">
                        <span className="ch-date-sep">
                          {new Date(msg.createdAt).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kolkata" })}
                        </span>
                      </div>
                    )}
                    {isSystem ? (
                      <div className="ch-system-card">
                        <div className="ch-system-body">{content}</div>
                        <div className="ch-system-label">Message by system</div>
                      </div>
                    ) : (
                      <div className={`ch-row ${isConsultantSide ? "ch-row--right" : "ch-row--left"}`}>
                        <div className={`ch-bubble ${isConsultantSide ? "ch-bubble--right" : "ch-bubble--left"}`}>
                          <div className="ch-msg-body">{content}</div>
                          <p className={`ch-msg-meta ${isConsultantSide ? "ch-msg-meta--right" : "ch-msg-meta--left"}`}>
                            {msg?.from?.name} :{dateFormatWithTime(msg.createdAt)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : chatHistoryModal.recordingUrl && String(chatHistoryModal.consultationMode || "").toLowerCase() === "audio" ? null : (
            <div className="ch-center">
              <span className="ch-empty-text">No messages found for this consultation window.</span>
            </div>
          )}
        </div>
      </Dialog>

      {/* ── History Dialog ── */}
      <Dialog open={historyModal.open} onClose={() => setHistoryModal((m) => ({ ...m, open: false }))} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle className="cp-log-dialog-title">
          Tag History — {historyModal.title}
        </DialogTitle>
        <DialogContent dividers>
          {historyModal.history?.length > 0 ? (
            <div className="cc-history-list">
              {[...historyModal.history].reverse().map((h, i) => (
                <div key={i} className="cc-history-row">
                  <span className="cc-hist-type">{h.tagType === "issueTag" ? "Tag 1 (Issue)" : h.tagType === "statusTag" ? "Tag 2 (Status)" : "Tag 3 (Category)"}</span>
                  <span className="cc-tag-badge" style={{ background: h.tagType === "issueTag" ? issueTagColor(h.value) : h.tagType === "statusTag" ? statusTagColor(h.value) : categoryTagColor(h.value) }}>
                    {h.tagType === "issueTag" ? (h.value === "issue" ? "Issue" : "No Issue") : h.tagType === "statusTag" ? statusTagLabel(h.value) : categoryTagLabel(h.value)}
                  </span>
                  {h.isAutomatic && <span className="cc-tag-auto">Auto (System)</span>}
                  <span className="cc-hist-meta">{dateFormat(h.setAt)} · {h.setByName}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="cp-muted">No tag history yet.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setHistoryModal((m) => ({ ...m, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerProblemIdentification;
